import {
    CLUB_CATEGORIES, CLUB_CATEGORY_LABELS, CLUB_CATEGORY_LABELS_PLURAL, MAX_CLUBS_PER_BAG,
} from '@golf/common';
import { Briefcase, Package, Plus, Star } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../lib/api.ts';
import type { Bag, Club, ClubTemplate } from '../../lib/equipment.ts';
import {
    activateBag, archiveBag, archiveClub,
    listBags, listClubs, listTemplates,
} from '../../lib/equipment.ts';
import { useSettings } from '../../lib/settings.tsx';
import { formatDistance } from '../../lib/units.ts';
import { ClubIcon } from '../../components/ChoixDuClub.tsx';
import { BagEditor } from './BagEditor.tsx';
import { ClubEditor } from './ClubEditor.tsx';
import { SeriesPicker } from './SeriesPicker.tsx';

type Tab = 'clubs' | 'bags';

export function MyEquipment() {
    const { unit } = useSettings();
    const [tab, setTab] = useState<Tab>('clubs');
    const [clubs, setClubs] = useState<Club[]>([]);
    const [bags, setBags] = useState<Bag[]>([]);
    const [templates, setTemplates] = useState<ClubTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [clubEditor, setClubEditor] = useState<{ open: boolean; club: Club | null }>({ open: false, club: null });
    const [bagEditor, setBagEditor] = useState<{ open: boolean; bag: Bag | null }>({ open: false, bag: null });
    const [seriesPicker, setSeriesPicker] = useState(false);

    const load = useCallback(async () => {
        try {
            const [c, b] = await Promise.all([listClubs(), listBags()]);
            setClubs(c);
            setBags(b);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Chargement impossible');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void load();
        void listTemplates().then(setTemplates).catch(() => { /* saisie libre possible */ });
    }, [load]);

    const onArchiveClub = async (c: Club): Promise<void> => {
        // Un club qui porte de l'historique ne se supprime pas : la base le
        // refuse et c'est voulu. On le dit avant de le tenter.
        const warning = c.shotCount > 0
            ? `${c.label} a ${c.shotCount} coup(s) enregistré(s). Il sera retiré de votre matériel et de vos sacs, mais son historique reste consultable.`
            : `Retirer ${c.label} de votre matériel ?`;
        if (!confirm(warning)) return;
        try {
            await archiveClub(c.id);
            await load();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Suppression impossible');
        }
    };

    const onArchiveBag = async (b: Bag): Promise<void> => {
        if (!confirm(`Supprimer le sac « ${b.name} » ? Vos clubs ne sont pas touchés.`)) return;
        try {
            await archiveBag(b.id);
            await load();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Suppression impossible');
        }
    };

    if (loading) {
        return <div className="flex justify-center py-16"><span className="loading loading-ring loading-lg text-primary" /></div>;
    }

    return (
        <div className="py-2">
            <header className="mb-4">
                <h1 className="font-display text-2xl font-bold tracking-tight">Mon matériel</h1>
            </header>

            {/* Ce qu'on vient vérifier d'un coup d'œil : combien de clubs,
                combien de compositions.

                La VALEUR a disparu. Elle occupait un tiers du bandeau pour un
                chiffre qui ne sert à rien sur un terrain — ni à choisir un
                club, ni à composer un sac. Le prix reste saisissable sur
                chaque fiche pour qui tient ses comptes ; il n'a simplement plus
                le rang d'un indicateur. */}
            <div className="mb-3 flex items-stretch rounded-box border border-base-300 bg-base-100 p-3 text-center shadow-sm">
                <div className="flex-1">
                    <div className="font-score text-2xl leading-none">{clubs.length}</div>
                    <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.12em] opacity-55">
                        club{clubs.length > 1 ? 's' : ''}
                    </div>
                </div>
                <div className="w-px bg-base-300" />
                <div className="flex-1">
                    <div className="font-score text-2xl leading-none">{bags.length}</div>
                    <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.12em] opacity-55">
                        sac{bags.length > 1 ? 's' : ''}
                    </div>
                </div>
            </div>

            {/* Deux onglets, une seule entrée de menu : l'inventaire et les
                compositions sont le même sujet pour le joueur. Les mêmes
                pastilles que les statistiques — c'est la même bascule. */}
            <div role="tablist" className="mb-3 flex gap-1 rounded-box bg-base-200 p-1">
                {([['clubs', 'Mes clubs'], ['bags', 'Mes sacs']] as const).map(([cle, mot]) => (
                    <button key={cle} role="tab" type="button" aria-selected={tab === cle}
                        onClick={() => setTab(cle)}
                        className={`flex-1 rounded-field py-2 text-sm font-bold transition-colors ${
                            tab === cle ? 'bg-base-100 shadow-sm' : 'opacity-60 hover:opacity-100'}`}>
                        {mot}
                    </button>
                ))}
            </div>

            {error && (
                <div role="alert" className="alert alert-error mb-4 text-sm">
                    <span>{error}</span>
                    <button type="button" className="btn btn-ghost btn-xs" onClick={() => setError(null)}>OK</button>
                </div>
            )}

            {tab === 'clubs' && (
                <>
                    {/* La série d'abord : c'est le geste qui fait gagner le plus
                        de temps, et le plus probable au premier lancement.
                        Saisir six fers un par un sur un téléphone est ce qui
                        fait abandonner avant d'avoir commencé. */}
                    {/* La série EN GRAND, le club à l'unité en ligne de texte
                        dessous. Les deux à égalité laissaient croire à deux
                        chemins équivalents : ils ne le sont pas. Saisir six fers
                        un par un sur un téléphone est ce qui fait abandonner
                        avant d'avoir commencé. */}
                    <button type="button"
                        className="btn btn-primary btn-block h-12 gap-2 font-bold tap-target"
                        onClick={() => setSeriesPicker(true)}>
                        <Package size={19} /> Ajouter une série
                    </button>
                    <button type="button"
                        className="btn btn-ghost btn-sm mx-auto mb-2 mt-1 flex gap-2 font-semibold"
                        onClick={() => setClubEditor({ open: true, club: null })}>
                        <Plus size={15} /> Un club à l'unité
                    </button>

                    {clubs.length === 0 ? (
                        <div className="rounded-box border border-dashed border-base-300 p-8 text-center">
                            <p className="text-sm opacity-60">
                                Votre matériel est vide.
                            </p>
                            <p className="mx-auto mt-2 max-w-sm text-sm opacity-60">
                                Si vous jouez avec une série du commerce — Inesis 900, 500 ou la
                                demi-série 100 — ajoutez-la d'un geste plutôt que club par club.
                            </p>
                        </div>
                    ) : (
                        // Groupés par famille — bois, fers, wedges — comme
                        // dans un vrai sac : c'est ainsi qu'on pense sa
                        // composition, pas dans l'ordre où les clubs ont été
                        // saisis.
                        <div>
                            {CLUB_CATEGORIES.map((cat) => {
                                /* Du plus long au plus court, comme dans un
                                   sac : c'est l'ordre dans lequel on cherche un
                                   club — « il me faut 150 m » — et non l'ordre
                                   alphabétique, qui plaçecait le bois 5 devant
                                   le driver. Sans distance déclarée, on ferme
                                   la famille : on ne peut pas la situer. */
                                const dansLaCategorie = clubs
                                    .filter((c) => c.category === cat)
                                    .sort((a, b) => (b.referenceDistanceM ?? -1) - (a.referenceDistanceM ?? -1));
                                if (dansLaCategorie.length === 0) return null;
                                return (
                                    <div key={cat}>
                                        <h2 className="mb-2 mt-5 flex items-center gap-2 px-0.5 text-[11px] font-bold uppercase tracking-[0.14em] text-primary first:mt-0">
                                            {dansLaCategorie.length > 1
                                                ? CLUB_CATEGORY_LABELS_PLURAL[cat]
                                                : CLUB_CATEGORY_LABELS[cat]}
                                            <span className="h-px flex-1 bg-base-300" />
                                        </h2>
                                        <div className="space-y-2">
                                            {dansLaCategorie.map((c) => {
                                                // La fiabilité, toute période confondue : la même
                                                // donnée qui choisit le club sur le parcours,
                                                // enfin lisible pour elle-même.
                                                const rate = c.shotCount > 0
                                                    ? Math.round((c.centerCount * 100) / c.shotCount) : null;
                                                /* Les mêmes seuils et les mêmes couleurs que la
                                                   jauge des statistiques : sept balles sur dix,
                                                   c'est un club sur lequel on bâtit un coup ; six,
                                                   un club qui passe ; en dessous, un club à
                                                   travailler. Le laiton d'ÉCRITURE, pas celui de
                                                   fond — en aplat pâle il tombe sous 2:1 sur le
                                                   papier. */
                                                const ton = rate == null ? undefined
                                                    : rate >= 70 ? 'var(--color-primary)'
                                                        : rate >= 60 ? 'var(--brass-ink)'
                                                            : 'var(--brique-bord)';
                                                return (
                                                    <button key={c.id} type="button"
                                                        onClick={() => setClubEditor({ open: true, club: c })}
                                                        className="flex w-full items-center gap-3 rounded-box border border-base-300
                                                                   bg-base-100 p-2.5 text-left shadow-sm transition-colors
                                                                   hover:bg-base-200 active:scale-[0.995]">
                                                        {/* Le même socle vert et le même fer que
                                                            sur l'écran de jeu : un club se reconnaît
                                                            à la même image partout. */}
                                                        <span className="flex size-11 shrink-0 items-center justify-center rounded-field bg-[var(--fairway-deep)] text-accent">
                                                            <ClubIcon size={21} />
                                                        </span>
                                                        <span className="min-w-0 flex-1">
                                                            {/* `leading-[1.15]` : Big Shoulders
                                                                Display descend sous la ligne de
                                                                base, et `leading-none` rasait le
                                                                jambage du « g ». */}
                                                            {/* Le nom SEUL. `clubDisplayLabel`
                                                                y accole la marque pour distinguer
                                                                deux fers 7 du sac — utile dans une
                                                                liste de choix, redondant ici où la
                                                                marque s'écrit juste dessous. On
                                                                lisait « Driver Inesis / Inesis 900 ».

                                                                Et `font-score`, la même condensée
                                                                que sur l'écran de jeu : un club porte
                                                                le même nom, dans la même lettre,
                                                                partout. */}
                                                            <span className="font-score block truncate text-xl leading-[1.15]">
                                                                {c.nickname?.trim() || c.label}
                                                            </span>
                                                            <span className="mt-0.5 block truncate text-xs opacity-60">
                                                                {[
                                                                    c.brand && c.model ? `${c.brand} ${c.model}` : c.brand || c.model,
                                                                    c.referenceDistanceM != null
                                                                        ? formatDistance(c.referenceDistanceM, unit) : null,
                                                                ].filter(Boolean).join(' \u00b7 ') || 'sans détail'}
                                                            </span>
                                                        </span>
                                                        {/* La fiabilité, ou son absence dite en
                                                            toutes lettres : « — » seul se lirait
                                                            comme zéro pour cent, et un club jamais
                                                            joué n'a pas zéro de fiabilité, il n'en
                                                            a aucune. */}
                                                        <span className="shrink-0 text-right">
                                                            <span className="font-score block text-lg leading-none tabular-nums"
                                                                style={ton ? { color: ton } : { opacity: 0.4 }}>
                                                                {rate != null ? `${rate}\u00a0%` : '—'}
                                                            </span>
                                                            <span className="mt-1 block text-[9px] font-bold uppercase tracking-[0.1em] opacity-50">
                                                                {rate != null ? 'fiabilité' : 'non joué'}
                                                            </span>
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </>
            )}

            {tab === 'bags' && (
                <>
                    <button type="button"
                        className="btn btn-primary btn-block mb-3 h-12 gap-2 font-bold tap-target"
                        onClick={() => setBagEditor({ open: true, bag: null })}>
                        <Plus size={19} /> Nouveau sac
                    </button>

                    {bags.length === 0 ? (
                        <p className="rounded-box border border-dashed border-base-300 p-8 text-center text-sm opacity-60">
                            Aucun sac. Un sac est une configuration nomm\u00e9e \u2014 \u00ab Parcours \u00bb,
                            \u00ab Practice \u00bb, \u00ab Hiver \u00bb \u2014 de {MAX_CLUBS_PER_BAG} clubs au maximum,
                            pioch\u00e9s dans votre mat\u00e9riel.
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {bags.map((b) => {
                                const trop = b.clubs.length > MAX_CLUBS_PER_BAG;
                                return (
                                    <section key={b.id}
                                        className="rounded-box border border-base-300 bg-base-100 p-3 shadow-sm">
                                        {/* L'ent\u00eate ouvre la fiche. Le crayon et la
                                            corbeille ont disparu de la carte : deux
                                            cibles de plus par sac, pos\u00e9es \u00e0 c\u00f4t\u00e9 du
                                            bouton qui active, sur un \u00e9cran qu'on
                                            manipule d'une main. Modifier, c'est
                                            toucher le sac ; supprimer vit dans la
                                            fiche. */}
                                        <button type="button"
                                            onClick={() => setBagEditor({ open: true, bag: b })}
                                            className="flex w-full items-center gap-3 text-left">
                                            {/* Le sac ACTIF porte le socle vert de
                                                l'application, les autres le sable : on
                                                voit lequel part au parcours sans lire
                                                une seule \u00e9tiquette. */}
                                            <span className="flex size-11 shrink-0 items-center justify-center rounded-field"
                                                style={b.isActive
                                                    ? { backgroundColor: 'var(--fairway-deep)', color: 'var(--color-accent)' }
                                                    : { backgroundColor: 'var(--tuile)', color: 'var(--tuile-encre)' }}>
                                                <Briefcase size={20} aria-hidden="true" />
                                            </span>
                                            <span className="min-w-0 flex-1">
                                                <span className="flex flex-wrap items-center gap-2">
                                                    <span className="font-score truncate text-xl leading-[1.15]">
                                                        {b.name}
                                                    </span>
                                                    {b.isActive && (
                                                        <span className="inline-flex shrink-0 items-center gap-1 rounded-field px-2 py-0.5 text-[11px] font-bold"
                                                            style={{ backgroundColor: 'var(--tuile)', color: 'var(--color-primary)' }}>
                                                            <Star size={11} aria-hidden="true" /> actif
                                                        </span>
                                                    )}
                                                </span>
                                                <span className="mt-0.5 block text-xs opacity-60">
                                                    {b.clubs.length} / {MAX_CLUBS_PER_BAG} clubs
                                                </span>
                                            </span>
                                        </button>

                                        {b.description && (
                                            <p className="mt-2 px-0.5 text-sm opacity-70">{b.description}</p>
                                        )}

                                        {/* La r\u00e8gle des quatorze clubs, MONTR\u00c9E plut\u00f4t
                                            qu'\u00e9crite en petit \u00e0 c\u00f4t\u00e9 du chiffre. La piste
                                            assombrit le fond au lieu de le peindre :
                                            une couleur fixe dispara\u00eet d\u00e8s que le fond
                                            change. */}
                                        <div className="mt-2 h-1.5 overflow-hidden rounded-full"
                                            style={{ background: 'color-mix(in oklab, var(--tuile-encre) 16%, transparent)' }}>
                                            <div className="h-full rounded-full"
                                                style={{
                                                    width: `${Math.min(100, (b.clubs.length / MAX_CLUBS_PER_BAG) * 100)}%`,
                                                    background: trop ? 'var(--brique-bord)' : 'var(--color-primary)',
                                                }} />
                                        </div>
                                        {trop && (
                                            <p className="mt-1.5 text-xs font-semibold"
                                                style={{ color: 'var(--brique-bord)' }}>
                                                {b.clubs.length - MAX_CLUBS_PER_BAG} club(s) de trop : la r\u00e8gle en
                                                autorise {MAX_CLUBS_PER_BAG}.
                                            </p>
                                        )}

                                        {b.clubs.length > 0 && (
                                            <div className="mt-2 flex flex-wrap gap-1.5">
                                                {b.clubs.map((c) => (
                                                    <span key={c.id}
                                                        className="rounded-field px-2 py-1 text-xs font-semibold"
                                                        style={{ backgroundColor: 'var(--tuile)', color: 'var(--tuile-encre)' }}>
                                                        {c.nickname?.trim() || c.label}
                                                    </span>
                                                ))}
                                            </div>
                                        )}

                                        {!b.isActive && (
                                            <button type="button"
                                                onClick={() => void activateBag(b.id).then(load)}
                                                className="mt-3 flex w-full items-center justify-center gap-2 rounded-field
                                                           border border-base-300 py-2.5 text-sm font-bold
                                                           transition-colors hover:bg-base-200">
                                                <Star size={15} aria-hidden="true" /> Utiliser ce sac
                                            </button>
                                        )}
                                    </section>
                                );
                            })}
                        </div>
                    )}
                </>
            )}

            {clubEditor.open && (
                <ClubEditor
                    club={clubEditor.club}
                    templates={templates}
                    onClose={() => setClubEditor({ open: false, club: null })}
                    onSaved={() => void load()}
                    onArchive={(c) => void onArchiveClub(c)}
                />
            )}
            {seriesPicker && (
                <SeriesPicker
                    onClose={() => setSeriesPicker(false)}
                    onAdded={() => void load()}
                />
            )}
            {bagEditor.open && (
                <BagEditor
                    bag={bagEditor.bag}
                    clubs={clubs}
                    onArchive={(b) => void onArchiveBag(b)}
                    onClose={() => setBagEditor({ open: false, bag: null })}
                    onSaved={() => void load()}
                />
            )}
        </div>
    );
}
